import React, { useState } from 'react';
import { Eye, EyeOff, CheckCircle2 } from 'lucide-react';

const strengthMeta = {
  Strong: { label: 'Strong', color: '#10b981', width: '100%' },
  Medium: { label: 'Medium', color: '#f59e0b', width: '60%'  },
  Weak:   { label: 'Weak',   color: '#ef4444', width: '30%'  },
};

/**
 * SetupPasswordForm — the actual first-time password creation form.
 * Props:
 *   onSuccess(token, userData) — called after the server confirms setup
 *   error — external error string (network / server errors from parent)
 *   loading — external loading state
 *   onSubmit(password, confirm) — async handler in parent
 */
export function SetupPasswordForm({ onSubmit, error, loading }) {
  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [showPass,  setShowPass]  = useState(false);
  const [success,   setSuccess]   = useState(false);

  const strength = password.length >= 12 ? 'Strong'
    : password.length >= 8 ? 'Medium'
    : password.length > 0  ? 'Weak' : '';
  const sm = strengthMeta[strength];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await onSubmit(password, confirm);
    if (ok) setSuccess(true);
  };

  if (success) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0' }}>
        <CheckCircle2 size={48} color="#10b981" style={{ margin: '0 auto 16px', display: 'block' }} />
        <div style={{ color: '#10b981', fontWeight: 800, fontSize: 16 }}>Password Set!</div>
        <div style={{ color: 'var(--auth-text-muted)', fontSize: 12, marginTop: 8 }}>Redirecting to dashboard…</div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Password field */}
      <div>
        <label className="auth-label">New Password</label>
        <div style={{ position: 'relative' }}>
          <input
            type={showPass ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Min. 8 characters"
            autoComplete="new-password"
            className={`auth-input ${error ? 'error' : ''}`}
            style={{ paddingRight: 44 }}
            data-testid="setup-password"
          />
          <button type="button" onClick={() => setShowPass(p => !p)} className="auth-show-pass-btn">
            {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        {sm && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <div style={{ flex: 1, height: 3, background: 'var(--auth-input-bg)', borderRadius: 2 }}>
              <div style={{ width: sm.width, height: '100%', background: sm.color, borderRadius: 2, transition: 'width 0.3s' }} />
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, color: sm.color }}>{sm.label}</span>
          </div>
        )}
      </div>

      {/* Confirm field */}
      <div>
        <label className="auth-label">Confirm Password</label>
        <input
          type="password"
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          placeholder="Re-enter password"
          autoComplete="new-password"
          className={`auth-input ${confirm && confirm !== password ? 'error' : ''}`}
          data-testid="setup-confirm"
        />
      </div>

      {error && <div className="auth-error-banner">{error}</div>}

      <button
        type="submit"
        disabled={loading || !password || !confirm}
        className="auth-submit"
        style={{ background: loading ? '#14532d' : '#10b981', boxShadow: '0 4px 16px rgba(16,185,129,0.3)' }}
        data-testid="setup-submit"
      >
        {loading ? 'Securing…' : 'Set Password & Enter System'}
      </button>
    </form>
  );
}
